import { useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import Checkbox from 'components/global/Checkbox';
import Icon from 'components/global/Icon';
import Tooltip from 'components/global/Tooltip';
import Typography from 'components/global/Typography';

import { faGripDotsVertical } from '@fortawesome/pro-solid-svg-icons';

import * as S from '../Table.styled';

const TableColumnsDndItem = (props) => {
	const { index, column, onColumnOrderChange, onColumnVisibilityChange, isSingleVisibleColumn } = props;
	const checboxDisabled = (isSingleVisibleColumn && column.show !== false) || column.enableHiding === false;
	const id = column.id ?? column.accessorKey;
	const ref = useRef(null);

	const [{ handlerId }, drop] = useDrop({
		accept: 'column',
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId()
			};
		},
		hover(item, monitor) {
			if (!ref.current) {
				return;
			}
			const dragIndex = item.index;
			const hoverIndex = index;
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return;
			}
			// Determine rectangle on screen
			const hoverBoundingRect = ref.current?.getBoundingClientRect();
			// Get vertical middle
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
			// Determine mouse position
			const clientOffset = monitor.getClientOffset();
			// Get pixels to the top
			const hoverClientY = clientOffset.y - hoverBoundingRect.top;
			// Only perform the move when the mouse has crossed half of the items height
			// When dragging downwards, only move when the cursor is below 50%
			// When dragging upwards, only move when the cursor is above 50%
			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return;
			}
			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return;
			}
			// Time to actually perform the action
			onColumnOrderChange(dragIndex, hoverIndex);

			// Note: we're mutating the monitor item here!
			// Generally it's better to avoid mutations,
			// but it's good here for the sake of performance
			// to avoid expensive index searches.
			item.index = hoverIndex;
		}
	});

	const [{ isDragging }, drag] = useDrag({
		type: 'column',
		item: () => ({ id, index }),
		collect: (monitor) => ({
			isDragging: monitor.isDragging()
		})
	});
	const opacity = isDragging ? 0 : 1;
	drag(drop(ref));

	return (
		<Tooltip
			title={
				checboxDisabled && column.enableHiding !== false
					? 'dynamiclist.atLeastOneColumnMustBeVisible'
					: 'dynamiclist.dragAndDropToReorderColumns'
			}
			timeout={checboxDisabled ? undefined : 3000}>
			<S.ColumnsMenuItem
				ref={ref}
				style={{ opacity }}
				data-handler-id={handlerId}
				className={column.show !== false ? 'selected' : ''}>
				<Checkbox
					checked={column.show !== false}
					onChange={() => onColumnVisibilityChange(column)}
					disabled={checboxDisabled}
				/>
				<Typography>{typeof column.header === 'string' ? column.header : column.id}</Typography>
				<Icon icon={faGripDotsVertical} size="large" color="var(--black)" />
			</S.ColumnsMenuItem>
		</Tooltip>
	);
};
export default TableColumnsDndItem;
